import { FC } from "react";
import {
	AgentCompanionExpression,
	AgentCompanionVariant,
} from "./agentCompanion.constants";

/**
 * The companion's artwork, drawn inline as SVG.
 *
 * Inline rather than an exported PNG on purpose: the character is not chosen
 * yet, so a binary asset would have to be re-cut three times, and SVG stays
 * crisp at any size with nothing added to the build. When design hands over the
 * final render, only this file changes — every consumer keeps the same props.
 *
 * Purely decorative. The caller marks the whole overlay `aria-hidden`; there is
 * no title or role in here for an assistive technology (or a Playwright
 * locator) to catch on.
 */

interface CompanionMascotProps {
	variant: AgentCompanionVariant;
	expression: AgentCompanionExpression;
	/** Rendered width in px; the drawing scales with it. */
	size?: number;
}

const INK = "#17303A";
const BLUSH = "#E8A0A0";

/** Where each candidate's face sits inside the shared 200x220 canvas. */
const FACE_ORIGIN: Record<AgentCompanionVariant, { x: number; y: number }> = {
	// In the head, not the middle of the creature — the leaf is the face.
	leaf: { x: 100, y: 74 },
	heart: { x: 100, y: 94 },
	pathfinder: { x: 100, y: 96 },
};

const Eyes: FC<{
	x: number;
	y: number;
	expression: AgentCompanionExpression;
}> = ({ x, y, expression }) => {
	const openEye = (cx: number, big: boolean) => (
		<g key={cx}>
			<ellipse
				cx={cx}
				cy={y}
				rx={big ? 10 : 9}
				ry={big ? 12 : 10.5}
				fill={INK}
			/>
			<circle cx={cx + 3.2} cy={y - 4} r={big ? 3.6 : 3} fill="#FFFFFF" />
		</g>
	);

	// A closed, contented eye is an upward curve — the same shape the sheet uses
	// for both "Proud" and the wink in "Encouraging".
	const closedEye = (cx: number) => (
		<path
			key={cx}
			d={`M${cx - 11} ${y + 2} Q${cx} ${y - 10} ${cx + 11} ${y + 2}`}
			stroke={INK}
			strokeWidth={4}
			strokeLinecap="round"
			fill="none"
		/>
	);

	const left = x - 21;
	const right = x + 21;

	if (expression === "proud") {
		return (
			<>
				{closedEye(left)}
				{closedEye(right)}
			</>
		);
	}

	if (expression === "encouraging") {
		return (
			<>
				{closedEye(left)}
				{openEye(right, false)}
			</>
		);
	}

	return (
		<>
			{openEye(left, expression === "excited")}
			{openEye(right, expression === "excited")}
		</>
	);
};

const Mouth: FC<{
	x: number;
	y: number;
	expression: AgentCompanionExpression;
}> = ({ x, y, expression }) => {
	const my = y + 21;

	// Open, rounded-off-the-bottom mouth for the two lively expressions.
	if (expression === "excited" || expression === "encouraging") {
		const w = expression === "excited" ? 16 : 13;
		const h = expression === "excited" ? 18 : 14;
		return (
			<path
				d={`M${x - w} ${my} H${x + w} A${w} ${h} 0 0 1 ${x - w} ${my} Z`}
				fill={INK}
			/>
		);
	}

	const w = expression === "proud" ? 11 : 14;
	return (
		<path
			d={`M${x - w} ${my} Q${x} ${my + (expression === "proud" ? 9 : 13)} ${x + w} ${my}`}
			stroke={INK}
			strokeWidth={4}
			strokeLinecap="round"
			fill="none"
		/>
	);
};

const Face: FC<{
	variant: AgentCompanionVariant;
	expression: AgentCompanionExpression;
}> = ({ variant, expression }) => {
	const { x, y } = FACE_ORIGIN[variant];
	return (
		<>
			<ellipse
				cx={x - 36}
				cy={y + 14}
				rx={9}
				ry={6}
				fill={BLUSH}
				opacity={0.5}
			/>
			<ellipse
				cx={x + 36}
				cy={y + 14}
				rx={9}
				ry={6}
				fill={BLUSH}
				opacity={0.5}
			/>
			<Eyes x={x} y={y} expression={expression} />
			<Mouth x={x} y={y} expression={expression} />
		</>
	);
};

/** The little white heart it wears — the through-line across all three. */
const HeartBadge: FC<{ x: number; y: number; scale: number; fill: string }> = ({
	x,
	y,
	scale,
	fill,
}) => (
	<path
		transform={`translate(${x} ${y}) scale(${scale}) translate(-100 -110)`}
		d="M100 196 C36 152 18 116 18 86 C18 56 42 36 68 36 C86 36 96 46 100 58 C104 46 114 36 132 36 C158 36 182 56 182 86 C182 116 164 152 100 196 Z"
		fill={fill}
	/>
);

/**
 * Option 1, "The Leaf Buddy" — the leaf is the head, on a body.
 *
 * Drawn to the character sheet's anatomy rather than as a single leaf with a
 * face on it: head, torso, two arms, two legs, heart on the chest. The one
 * constraint the sheet does not have to care about is size — this is rendered
 * at 64px beside a form field, where the whole 200x220 canvas is about 64px
 * wide and a slim limb disappears. So everything is short and thick, and the
 * silhouette does the work: head wider than body, body wider than legs.
 *
 * Order matters. Body and limbs are drawn before the head so the head overlaps
 * the shoulders, which is what makes the neck read without drawing one.
 */
const LeafBuddy: FC<{ expression: AgentCompanionExpression }> = ({
	expression,
}) => {
	// The raised arm belongs to the two poses that gesture — "You Got This!" and
	// the wave. The other two rest at the side.
	const armUp = expression === "encouraging" || expression === "excited";
	return (
		<g>
			{/* Legs, then arms, then torso: each tucks behind the part above it. */}
			<rect x={74} y={190} width={21} height={26} rx={10.5} fill="#6F9440" />
			<rect x={105} y={190} width={21} height={26} rx={10.5} fill="#6F9440" />

			<rect
				x={28}
				y={140}
				width={42}
				height={19}
				rx={9.5}
				fill="#7CA347"
				transform="rotate(22 49 149)"
			/>
			<rect
				x={130}
				y={armUp ? 96 : 140}
				width={42}
				height={19}
				rx={9.5}
				fill="#7CA347"
				transform={`rotate(${armUp ? -54 : -22} 151 ${armUp ? 105 : 149})`}
			/>

			{/* Tall enough that the arms sit on a torso rather than on a ball. The
			    legs at 190 put the feet at 216 on a 220 canvas, so this is as long
			    as the body goes without resizing the canvas — which would change
			    how every call site renders. */}
			<rect x={58} y={116} width={84} height={80} rx={34} fill="#8FB457" />

			{/* The head. Its base overlaps the torso, so no neck is needed, and the
			    tip leans and hooks like a real leaf rather than coming to a
			    symmetrical point — it is the whole reason the silhouette reads as a
			    leaf at 64px. */}
			<path
				d="M108 4 C118 16 112 26 104 30 C74 44 48 78 54 104 C60 128 78 140 100 140 C122 140 140 128 146 104 C152 74 130 28 108 4 Z"
				fill="#93B85C"
			/>
			{/* Midrib and veins, kept faint so they never fight the face. */}
			<g stroke="#6F9440" strokeWidth={3} strokeLinecap="round" opacity={0.35}>
				<path d="M104 30 Q100 84 100 134" />
				<path d="M101 56 L80 74" />
				<path d="M101 56 L122 74" />
			</g>

			<Face variant="leaf" expression={expression} />
			<HeartBadge x={100} y={154} scale={0.15} fill="#FFFFFF" />
		</g>
	);
};

const HeartHugger: FC<{ expression: AgentCompanionExpression }> = ({
	expression,
}) => (
	<g>
		<rect x={74} y={176} width={19} height={24} rx={9.5} fill="#7FBBAC" />
		<rect x={107} y={176} width={19} height={24} rx={9.5} fill="#7FBBAC" />

		<path
			d="M100 188 C42 146 24 112 24 84 C24 54 46 34 70 34 C87 34 96 43 100 55 C104 43 113 34 130 34 C154 34 176 54 176 84 C176 112 158 146 100 188 Z"
			fill="#A9D4C9"
		/>

		<Face variant="heart" expression={expression} />

		{/* The heart it is holding — the whole point of this candidate. */}
		<HeartBadge x={100} y={150} scale={0.3} fill="#3E6B94" />
		<rect
			x={40}
			y={132}
			width={36}
			height={16}
			rx={8}
			fill="#8FC6B6"
			transform="rotate(28 58 140)"
		/>
		<rect
			x={124}
			y={132}
			width={36}
			height={16}
			rx={8}
			fill="#8FC6B6"
			transform="rotate(-28 142 140)"
		/>
	</g>
);

const Pathfinder: FC<{ expression: AgentCompanionExpression }> = ({
	expression,
}) => (
	<g>
		{/* Backpack first so it sits behind the body. */}
		<ellipse cx={150} cy={158} rx={17} ry={22} fill="#C89B6A" />
		<ellipse cx={50} cy={158} rx={17} ry={22} fill="#C89B6A" />
		<rect x={54} y={136} width={92} height={72} rx={34} fill="#1F7A72" />
		{/* Shoulder strap, so the pack behind reads as a pack. */}
		<path
			d="M74 140 L118 200"
			stroke="#C89B6A"
			strokeWidth={9}
			strokeLinecap="round"
		/>
		<circle cx={100} cy={92} r={72} fill="#1F7A72" />
		{/* Hood tip — the sprout that ties this candidate to the other two. */}
		<path
			d="M150 34 C168 24 180 30 178 46 C176 60 162 66 150 58 Z"
			fill="#2E9E92"
		/>
		<circle cx={100} cy={96} r={55} fill="#FAF6EE" />

		<Face variant="pathfinder" expression={expression} />

		{/* Compass — guidance, direction, staying on course. */}
		<circle
			cx={100}
			cy={172}
			r={17}
			fill="#FAF6EE"
			stroke="#14554F"
			strokeWidth={3}
		/>
		<path d="M100 160 L106 172 L100 184 L94 172 Z" fill="#E4674E" />
		<rect
			x={22}
			y={148}
			width={34}
			height={16}
			rx={8}
			fill="#2E9E92"
			transform="rotate(16 39 156)"
		/>
	</g>
);

const VARIANT_ART: Record<
	AgentCompanionVariant,
	FC<{ expression: AgentCompanionExpression }>
> = {
	leaf: LeafBuddy,
	heart: HeartHugger,
	pathfinder: Pathfinder,
};

export const CompanionMascot: FC<CompanionMascotProps> = ({
	variant,
	expression,
	size = 128,
}) => {
	const Art = VARIANT_ART[variant];
	return (
		<svg
			width={size}
			height={size * 1.1}
			viewBox="0 0 200 220"
			fill="none"
			focusable="false"
			aria-hidden="true"
		>
			<Art expression={expression} />
		</svg>
	);
};

export default CompanionMascot;
